/**
 * TableDesignerDialog
 *
 * Dialog for opening the Table Designer - either creating a new table
 * or editing an existing one.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, Table, Plus, ArrowRight, Settings, X, Database } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@/components/ui/select';
import { useAvailableTables, type TableInfo } from '../../table-explorer/hooks/useAvailableTables';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useDatabaseList } from '@/features/database-objects/hooks/useDatabaseList';
import { FallbackSelectContent } from '@/features/connection-diagnostics/components/FallbackSelectContent';
import type { UserFacingEngineId } from '@/core/engines';
import { toast } from '@/components/ui/toaster';
import { synthesizeAlterTableBuffer, synthesizeBlankCreateTable } from '../services/synthesizeBuffer';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';
type TableDesignerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  database: string;
  /** Table currently selected in the explorer (shown as quick-access option) */
  explorerSelectedTable?: {
    name: string;
    schema: string;
    database: string;
  };
};

const MIN_TILE_WIDTH = 180;

export function TableDesignerDialog({
  open,
  onOpenChange,
  connectionId,
  database: initialDatabase,
  explorerSelectedTable,
}: TableDesignerDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Local database state - allows changing database within the dialog
  const [currentDatabase, setCurrentDatabase] = useState(initialDatabase);
  const [isDatabaseDropdownOpen, setIsDatabaseDropdownOpen] = useState(false);

  const { connections, activeConnections } = useConnectionStore();
  const { addTab, updateTabContent } = useWorkspaceStore();

  // Get connection info
  // Browser mode SQLite connections only exist in activeConnections, not in saved connections
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  const savedConnection = connections.find((c) => c.id === connectionId);
  const engine: UserFacingEngineId = (activeConnection?.connection.engine as UserFacingEngineId) || 'mssql';
  const defaultSchema = engine === 'pgsql' ? 'public' : (engine === 'sqlite' ? '' : 'dbo');

  // Shared per-connection listing (`useDatabaseList`): the dropdown paints the
  // databases the Object Explorer already listed instead of showing
  // "Loading databases…" over them, and still revalidates once per open.
  const {
    databases,
    state: databasesState,
    isInitialLoading: isDatabasesLoading,
  } = useDatabaseList({
    connectionId,
    engine,
    enabled: isDatabaseDropdownOpen && !!connectionId,
  });

  // Reset to initial database when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentDatabase(initialDatabase);
    }
  }, [open, initialDatabase]);

  // Fetch available tables
  const { data: allTables = [], isLoading } = useAvailableTables({
    connectionId,
    database: currentDatabase,
    engineId: engine,
    enabled: open && !!connectionId && !!currentDatabase,
  });

  // Filter to tables only (not views) and apply search
  const filteredTables = useMemo(() => {
    let result = allTables.filter((obj) => obj.objectType === 'table');

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (obj) =>
          obj.name.toLowerCase().includes(query) ||
          obj.schema.toLowerCase().includes(query)
      );
    }

    return result;
  }, [allTables, searchQuery]);

  // Calculate columns per row dynamically
  const getColumnsPerRow = useCallback(() => {
    if (!gridRef.current) return 3;
    const gridWidth = gridRef.current.clientWidth;
    const gap = 8;
    return Math.max(1, Math.floor((gridWidth + gap) / (MIN_TILE_WIDTH + gap)));
  }, []);

  // Reset state when dialog opens or when connection/database changes
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIndex(0);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open, connectionId, initialDatabase]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle creating a new table
  const handleCreateNewTable = useCallback(() => {
    // Browser mode SQLite connections only exist in activeConnections
    if (!activeConnection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    const blankSql = synthesizeBlankCreateTable({
      engine,
      schema: defaultSchema,
      tableName: '',
    });

    addTab({
      title: 'New Table',
      type: 'table-designer',
      content: {
        mode: 'create',
        tableName: '',
        schema: defaultSchema,
        database: currentDatabase,
        initialSql: blankSql,
        currentSql: blankSql,
        isDirty: false,
        isLoading: false,
      },
      connectionInfo: {
        connectionId,
        connectionName: safeConnectionAlias(savedConnection?.alias ? savedConnection : activeConnection.connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: currentDatabase,
      },
    });

    onOpenChange(false);
  }, [addTab, activeConnection, savedConnection, connectionId, currentDatabase, defaultSchema, engine, onOpenChange]);

  // Handle editing an existing table
  const handleEditTable = useCallback(
    async (table: TableInfo) => {
      // Browser mode SQLite connections only exist in activeConnections
      if (!activeConnection) {
        toast.error({ title: 'Error', description: 'Connection not found' });
        return;
      }

      // Create tab in loading state
      const tabId = addTab({
        title: `${table.name} [Design]`,
        type: 'table-designer',
        content: {
          mode: 'alter',
          tableName: table.name,
          schema: table.schema || defaultSchema,
          database: currentDatabase,
          isLoading: true,
        },
        connectionInfo: {
          connectionId,
          connectionName: safeConnectionAlias(savedConnection?.alias ? savedConnection : activeConnection.connection, 'Unnamed Connection'),
          serverHost: safeConnectionServer(activeConnection.connection),
          database: currentDatabase,
        },
      });

      onOpenChange(false);

      const result = await synthesizeAlterTableBuffer(
        {
          engine,
          database: currentDatabase,
          schema: table.schema || defaultSchema,
          tableName: table.name,
        },
        connectionId,
      );

      if (result.error) {
        updateTabContent(tabId, {
          error: result.error,
          isLoading: false,
        });
        return;
      }

      updateTabContent(tabId, {
        initialSql: result.sql ?? undefined,
        currentSql: result.sql ?? undefined,
        isLoading: false,
      });
    },
    [addTab, activeConnection, savedConnection, connectionId, currentDatabase, defaultSchema, engine, onOpenChange, updateTabContent]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredTables.length === 0) return;

      const totalItems = filteredTables.length;
      const columnsPerRow = getColumnsPerRow();

      switch (e.key) {
        case 'ArrowRight': {
          e.preventDefault();
          const nextIndex = selectedIndex + 1;
          if (nextIndex < totalItems) setSelectedIndex(nextIndex);
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          const prevIndex = selectedIndex - 1;
          if (prevIndex >= 0) setSelectedIndex(prevIndex);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = selectedIndex + columnsPerRow;
          if (nextIndex < totalItems) setSelectedIndex(nextIndex);
          else if (selectedIndex < totalItems - 1) setSelectedIndex(totalItems - 1);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = selectedIndex - columnsPerRow;
          if (prevIndex >= 0) setSelectedIndex(prevIndex);
          else if (selectedIndex > 0) setSelectedIndex(0);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (filteredTables[selectedIndex]) {
            void handleEditTable(filteredTables[selectedIndex]);
          }
          break;
        }
      }
    },
    [filteredTables, selectedIndex, getColumnsPerRow, handleEditTable]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (!containerRef.current) return;
    const selectedCard = containerRef.current.querySelector('[data-selected="true"]');
    if (selectedCard) {
      selectedCard.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const tableCount = allTables.filter((t) => t.objectType === 'table').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col" data-testid="table-designer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Table Designer
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col" onKeyDown={handleKeyDown} tabIndex={-1}>
          {/* Create New Table Section */}
          <div className="mb-4 pb-4 border-b border-border">
            <Button
              variant="outline"
              size="lg"
              className="w-full justify-start gap-3 h-14 border-primary/50 bg-primary/5 hover:bg-primary/10"
              onClick={handleCreateNewTable}
              data-testid="create-new-table-button"
            >
              <div className="h-10 w-10 rounded-md bg-primary/20 flex items-center justify-center">
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-base font-medium">Create New Table</span>
                <span className="text-xs text-muted-foreground">Design a new table from scratch</span>
              </div>
            </Button>
          </div>

          {/* Quick Access - Explorer Selected Table */}
          {explorerSelectedTable && explorerSelectedTable.database.toLowerCase() === currentDatabase.toLowerCase() && (
            <div className="mb-4">
              <div className="text-xs text-muted-foreground mb-2">Quick Access (from Object Explorer)</div>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-2 px-3"
                onClick={() =>
                  handleEditTable({
                    name: explorerSelectedTable.name,
                    schema: explorerSelectedTable.schema,
                    database: explorerSelectedTable.database,
                    objectType: 'table',
                  })
                }
                data-testid="quick-access-design-button"
              >
                <ArrowRight className="h-4 w-4 text-primary" />
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{explorerSelectedTable.name}</span>
                  <span className="text-xs text-muted-foreground">{explorerSelectedTable.schema}</span>
                </div>
              </Button>
            </div>
          )}

          {/* Edit Existing Table Section */}
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Database Selector */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">
                Edit Existing Table
                <span className="text-muted-foreground ml-2">({tableCount} tables)</span>
              </div>
              <Select
                value={currentDatabase}
                onValueChange={setCurrentDatabase}
                onOpenChange={setIsDatabaseDropdownOpen}
                open={isDatabaseDropdownOpen}
              >
                <SelectTrigger
                  data-testid="table-designer-database-dropdown"
                  className="h-8 w-auto min-w-[160px] max-w-[280px] px-2.5 gap-2 bg-muted/50 border-border hover:bg-muted transition-colors"
                >
                  <Database className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{currentDatabase || 'Select database'}</span>
                </SelectTrigger>
                <SelectContent
                  data-testid="table-designer-database-content"
                  className="max-h-60 overflow-auto min-w-[160px]"
                  position="popper"
                  align="end"
                >
                  {isDatabasesLoading ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                      <RandomDotmSpinner size={16} />
                      Loading...
                    </div>
                  ) : (
                    <FallbackSelectContent
                      connectionId={connectionId}
                      state={databasesState}
                      databases={databases}
                      isLoading={isDatabasesLoading}
                      selectedDatabase={currentDatabase}
                      testIdPrefix="designer"
                      beforeOpenDetails={() => setIsDatabaseDropdownOpen(false)}
                    />
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Search Input */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tables..."
                className="w-full pl-9 pr-16 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="table-designer-search-input"
              />
              {searchQuery && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {filteredTables.length} match{filteredTables.length !== 1 ? 'es' : ''}
                </span>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <RandomDotmSpinner size={20} className="text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading tables...</span>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredTables.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? `No tables match "${searchQuery}"`
                    : 'No tables found in this database'}
                </p>
              </div>
            )}

            {/* Table Grid */}
            {!isLoading && filteredTables.length > 0 && (
              <div ref={containerRef} className="flex-1 overflow-y-auto">
                <div
                  ref={gridRef}
                  className="grid gap-2"
                  style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_TILE_WIDTH}px, 1fr))` }}
                >
                  {filteredTables.map((table, index) => (
                    <button
                      key={`${table.schema}.${table.name}`}
                      type="button"
                      onClick={() => handleEditTable(table)}
                      data-selected={index === selectedIndex}
                      data-testid={`table-design-card-${table.schema}-${table.name}`}
                      className={`
                        flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition-[background-color,border-color,box-shadow] text-left w-full
                        hover:bg-accent hover:border-accent-foreground/20
                        focus:outline-none focus:ring-2 focus:ring-ring
                        ${index === selectedIndex ? 'bg-accent border-primary ring-1 ring-primary/50' : 'bg-card border-border'}
                      `}
                    >
                      <Table className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">{table.name}</span>
                        <span className="text-[10px] text-muted-foreground truncate block">{table.schema}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Keyboard hint */}
                <div className="text-center mt-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">↑↓←→</kbd>
                    navigate
                    <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">Enter</kbd>
                    select
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
